import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * FilterModalWrapper - Reusable centered modal wrapper for desktop filter modals
 * Provides consistent styling, animations, and behavior for all filter modals
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string} title - Modal title
 * @param {Component} icon - Icon component to display in header
 * @param {ReactNode} children - Modal content
 * @param {string} maxWidth - Maximum width ('sm', 'md', 'lg')
 * @param {string} maxHeight - Maximum height (CSS value)
 */
const FilterModalWrapper = ({
  isOpen,
  onClose,
  title,
  icon: Icon,
  children,
  maxWidth = 'md',
  maxHeight = '80vh'
}) => {
  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const widthClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{
                type: 'spring',
                duration: 0.3,
                bounce: 0.2
              }}
              className={`
                bg-gray-900 rounded-2xl shadow-2xl w-full
                ${widthClasses[maxWidth]} overflow-hidden
                border border-white/10 pointer-events-auto
              `}
              style={{ maxHeight }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/20">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  {Icon && <Icon size={20} className="text-emerald-400" />}
                  {title}
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  aria-label="Close modal"
                >
                  <X size={20} className="text-white/70" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto" style={{ maxHeight: `calc(${maxHeight} - 80px)` }}>
                {children}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FilterModalWrapper;
